<template>
  <div class="prefs-menu">
    <div class="prefs-row">
      <label class="prefs-label" :for="ids.oddsView">Betting View</label>
      <select
        :id="ids.oddsView"
        v-model="oddsView"
        class="prefs-select"
        name="odds"
        @change="updateOddsView()"
      >
        <option v-for="odds in oddsViews" :key="odds.key" :value="odds">
          {{ odds.name }}
        </option>
      </select>
    </div>
    <div class="prefs-row">
      <label class="prefs-label" :for="ids.oddsType">Odds Display</label>
      <select
        :id="ids.oddsType"
        v-model="oddsType"
        name="oddsTypes"
        class="prefs-select"
        @change="updateOddsType()"
      >
        <option v-for="format in oddsTypes" :key="format.key" :value="format">
          {{ format.name }}
        </option>
      </select>
    </div>
    <div class="prefs-row">
      <label class="prefs-label" :for="ids.sortByV">Sort By</label>
      <select
        :id="ids.sortByV"
        v-model="sortByValue"
        name="sortBy"
        class="prefs-select"
        @change="updateSortBy()"
      >
        <option v-for="value in sortBy" :key="value.key" :value="value">
          {{ value.name }}
        </option>
      </select>
    </div>
    <div class="prefs-row">
      <label class="prefs-label" :for="ids.rotation">Rotation #s</label>
      <select
        :id="ids.rotation"
        v-model="displayRotation"
        name="rotationNumbers"
        class="prefs-select"
        @change="updateDisplayRotationNumbers()"
      >
        <option
          v-for="display in displayRotationOptions"
          :key="display.key"
          :value="display"
        >
          {{ display.name }}
        </option>
      </select>
    </div>
    <div class="prefs-row">
      <label class="prefs-label" :for="ids.commission">
        Odds w/ Commission
      </label>
      <select
        :id="ids.commission"
        v-model="viewOddsWithCommission"
        name="viewCommission"
        class="prefs-select"
        @change="handleViewOddsWithCommission()"
      >
        <option
          v-for="display in viewCommissionOptions"
          :key="display.key"
          :value="display"
        >
          {{ display.name }}
        </option>
      </select>
    </div>
    <div class="prefs-row">
      <label class="prefs-label" :for="ids.theme">Theme</label>
      <select
        :id="ids.theme"
        v-model="selectedTheme"
        name="display"
        class="prefs-select"
      >
        <option v-for="theme in themes" :key="theme.id" :value="theme.key">
          {{ theme.name }}
        </option>
      </select>
    </div>
    <div v-if="showCancelAll" class="prefs-cancel-all-container">
      <button class="prefs-cancel-all" @click="emit('cancel-all')">
        <font-awesome-icon :icon="faTrashCan" class="mr-2" />
        Cancel All Offers
      </button>
    </div>
  </div>
</template>

<script setup>
// The one user-preference panel: betting view, odds format, sort, rotation
// numbers, commission display and theme. Lifted out of LeagueHeader so the
// header bar and the homepage welcome band open the SAME control set rather
// than drifting copies. The panel owns the controls and the persistence; the
// parent owns where it sits and when it's visible.
import Swal from 'sweetalert2';
import { computed, ref, useId } from 'vue';
import { useRoute } from 'vue-router';
import { useAPI } from '@/composables/useAPI';
import { useUserStore } from '@/stores/user';
import { emitter } from '@/core/emitter';
import { checkAuthAndShowLogin } from '@/core/authUtils';
import {
  oddsViews,
  oddsTypes,
  sortBy,
  displayRotationOptions,
  viewCommissionOptions,
} from '@/consts/constants';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';

defineProps({
  // The header bar keeps its destructive action; surfaces that already have
  // one (or shouldn't) can opt out.
  showCancelAll: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const emit = defineEmits(['close', 'cancel-all']);

const route = useRoute();
const userStore = useUserStore();

// This panel can be mounted more than once on a page (header bar + homepage
// band), so label/control ids have to be per-instance or the labels all point
// at the first copy's controls.
const uid = useId();
const ids = {
  oddsView: `${uid}-odds-view`,
  oddsType: `${uid}-odds-type`,
  sortByV: `${uid}-sort-by`,
  rotation: `${uid}-rotation`,
  commission: `${uid}-commission`,
  theme: `${uid}-theme`,
};

const oddsView = ref();
const oddsType = ref();
const sortByValue = ref();
const displayRotation = ref();
const viewOddsWithCommission = ref();

const themes = [
  { name: 'Light mode', key: 'day', id: '2' },
  { name: 'Night mode', key: 'night', id: '1' },
];

const selectedTheme = computed({
  get() {
    return themes.find((theme) => theme.key === userStore.activeTheme)?.key;
  },
  set(value) {
    userStore.setActiveTheme(value);
  },
});

// initialize setting values
oddsView.value = userStore.user.sportsbookDefault ? oddsViews[0] : oddsViews[1];
if (userStore.user.oddsFormat !== undefined) {
  oddsType.value =
    userStore.user.oddsFormat === 'american'
      ? oddsTypes[0]
      : userStore.user.oddsFormat === 'decimal'
        ? oddsTypes[1]
        : oddsTypes[2];
} else {
  oddsType.value = oddsTypes[0];
}
if (userStore.user.defaultRotationNumbers) {
  sortByValue.value = sortBy[1];
} else if (userStore.user.matchedVolume) {
  sortByValue.value = sortBy[2];
} else if (userStore.user.openInterest) {
  sortByValue.value = sortBy[3];
} else {
  sortByValue.value = sortBy[0];
}
displayRotation.value = userStore.user.displayRotationNumbers
  ? displayRotationOptions[0]
  : displayRotationOptions[1];

viewOddsWithCommission.value = userStore.user.viewOddsWithCommission
  ? viewCommissionOptions[0]
  : viewCommissionOptions[1];

// functions to update settings
async function updateOddsView() {
  try {
    if (!checkAuthAndShowLogin()) {
      return;
    }
    const { request } = useAPI('user/updateDefaultView/');
    const newSBDefaultVal = oddsView.value.key === 'sportsbook';
    const response = await request({
      sportsbookDefault: newSBDefaultVal,
    });

    if (response.successful) {
      userStore.user.sportsbookDefault = newSBDefaultVal;
      // The all-props page has no sport/league params; map view-switch to its
      // sportsbook/exchange route variants instead of building /undefined/undefined.
      const onAllProps = [
        'AllProps',
        'AllPropsExchange',
        'AllPropsSportsbook',
      ].includes(route.name);
      if (onAllProps) {
        window.location.href =
          oddsView.value.key === 'sportsbook'
            ? '/sportsbook/all-props'
            : '/exchange/all-props';
      } else if (route.params.sport && route.params.league) {
        window.location.href =
          oddsView.value.key === 'sportsbook'
            ? `/${route.params.sport}/${route.params.league}`
            : `/exchange/${route.params.sport}/${route.params.league}`;
      }
      // Pages without sport/league params (e.g. the homepage) render the same
      // in both views — stay put rather than navigate to /undefined/undefined.
    }
  } catch {
    console.error('error updating odds view');
  }
}

async function updateOddsType() {
  try {
    if (!checkAuthAndShowLogin()) {
      return;
    }
    const { request } = useAPI('user/updateOddsFormat/');
    const response = await request({ oddsFormat: oddsType.value.key });

    if (response.oddsFormatUpdated) {
      userStore.user.oddsFormat =
        oddsType.value.key === 'american'
          ? 'american'
          : oddsType.value.key === 'decimal'
            ? 'decimal'
            : 'impliedProbability';
    }
  } catch {
    console.error('error updating odds type ');
  } finally {
    emit('close');
  }
}

async function updateSortBy() {
  try {
    if (!checkAuthAndShowLogin()) {
      return;
    }
    const { request } = useAPI('user/updateSortBy/');
    const { get } = useAPI('user/getMe/');
    await request({
      sortBy: sortByValue.value,
    });
    const getMeResponse = await get();
    userStore.setGetMe(getMeResponse.user);
    emitter.emit('sortChange', {});
  } catch {
    console.error('error updating sort by ');
  }
}

async function updateViewOddsWithCommission() {
  try {
    const { request } = useAPI('user/viewOddsWithCommission/');
    const response = await request({
      display: viewOddsWithCommission.value,
    });
    if (response.success) {
      userStore.user.viewOddsWithCommission =
        viewOddsWithCommission.value.key === 'yes';
    }
  } catch {
    console.error('error updating view odds with commission');
  } finally {
    emit('close');
  }
}

async function handleViewOddsWithCommission() {
  if (!checkAuthAndShowLogin()) {
    return;
  }
  if (!userStore.user.viewOddsWithCommission) {
    Swal.fire({
      html: '<h4>View Odds With Commission mode will display all odds with commission factored in, adjusted from the original price they were offered at. </h4>',
      showCancelButton: true,
      confirmButtonText: 'Update Display',
      confirmButtonColor: '#6f75b1',
    }).then((result) => {
      if (result.isConfirmed) {
        updateViewOddsWithCommission();
      } else {
        viewOddsWithCommission.value = userStore.user.viewOddsWithCommission
          ? viewCommissionOptions[0]
          : viewCommissionOptions[1];
      }
    });
  } else {
    Swal.fire({
      html: '<h4>Removing View Odds With Commission mode will display all odds without commission factored in, at the original price they were offered at. </h4>',
      showCancelButton: true,
      confirmButtonText: 'Update Display',
      confirmButtonColor: '#6f75b1',
    }).then((result) => {
      if (result.isConfirmed) {
        updateViewOddsWithCommission();
      } else {
        viewOddsWithCommission.value = userStore.user.viewOddsWithCommission
          ? viewCommissionOptions[0]
          : viewCommissionOptions[1];
      }
    });
  }
}

async function updateDisplayRotationNumbers() {
  try {
    if (!checkAuthAndShowLogin()) {
      return;
    }
    const { request } = useAPI('user/displayRotationNumbers/');
    const response = await request({
      display: displayRotation.value,
    });
    if (response.success) {
      userStore.user.displayRotationNumbers =
        displayRotation.value.key === 'yes';
    }
  } catch {
    console.error('error updating display rotation setting');
  } finally {
    emit('close');
  }
}
</script>

<style scoped lang="scss">
@use '@/scss/tokens.scss' as main;

.prefs-menu {
  padding: 0.5rem 0;
}

// Metrics deliberately match what the header popup rendered before this panel
// was extracted (its rows picked up padding/type from a generic
// `.popup-menu div` rule), so the mobile header dropdown is unchanged.
.prefs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.5rem;
  gap: 1rem;
  font-size: main.$font-size-sm;
  line-height: 1.5;
}

.prefs-label {
  font-family: 'Roboto Slab';
  font-weight: 700;
  font-size: 0.8rem;
  color: main.$color-primary_01;
  white-space: nowrap;
  flex-shrink: 0;
}

.prefs-select {
  font-family: 'Roboto Slab';
  text-align: left;
  color: main.$color-primary_01;
  font-weight: 700;
  border: none;
  border-radius: 5px;
  border-bottom: main.$border-09;
  cursor: pointer;
  background: main.$background-01;
  appearance: none;
  -webkit-appearance: none;
  background-size: 12px;
  background-position-x: calc(100% - 10px);
  width: 130px;
  min-width: 0;
  height: 32px;
  font-size: 0.8rem;
  padding-left: 8px;

  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    outline: 2px solid main.$color-primary_05 !important;
    outline-offset: 1px;
  }
}

.prefs-cancel-all-container {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.6rem 1.5rem;
  border-top: 1px solid main.$grey-300;
  margin-top: 0.5rem;
}

.prefs-cancel-all {
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: main.$color-primary_05;
  color: main.$white;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: main.$blue-800_02;
  }

  &:active {
    transform: scale(0.98);
  }
}
</style>
